import React from 'react';

/**
 * Simplified, brand-coloured payment method logos rendered as inline SVG.
 * Kept dependency-free so the strip stays crisp at any size and needs no
 * external image assets.
 *
 * The set mirrors the major payment methods supported through Stripe, which
 * gives InvoicePoint worldwide coverage.
 */

const FONT = "Poppins, 'Segoe UI', Arial, sans-serif";

function Logo({ label, width, children }) {
    return (
        <svg
            className="pay-logo-svg"
            width={width}
            height="20"
            viewBox={`0 0 ${width} 20`}
            role="img"
            aria-label={label}
            focusable="false"
        >
            {children}
        </svg>
    );
}

function PlainTextLogo({ label, text, color, width, fontSize = 13, fontWeight = 700, fontStyle }) {
    return (
        <Logo label={label} width={width}>
            <text
                x="0"
                y="15"
                fontFamily={FONT}
                fontSize={fontSize}
                fontWeight={fontWeight}
                fontStyle={fontStyle}
                fill={color}
            >
                {text}
            </text>
        </Logo>
    );
}

/* ------------------------------- Cards ------------------------------- */

export function VisaLogo() {
    return <PlainTextLogo label="Visa" text="VISA" color="#1a1f71" width="42" fontSize="15" fontWeight="800" fontStyle="italic" />;
}

export function MastercardLogo() {
    return (
        <Logo label="Mastercard" width="98">
            <circle cx="8" cy="10" r="7" fill="#eb001b" />
            <circle cx="16" cy="10" r="7" fill="#f79e1b" />
            <path fill="#ff5f00" d="M12 4.6a7 7 0 0 0 0 10.8 7 7 0 0 0 0-10.8z" />
            <text x="27" y="14.5" fontFamily={FONT} fontSize="11.5" fontWeight="600" fill="#1a1a2e">mastercard</text>
        </Logo>
    );
}

export function AmexLogo() {
    return (
        <Logo label="American Express" width="30">
            <rect x="0" y="1" width="28" height="18" rx="3" fill="#006fcf" />
            <text x="4.4" y="14" fontFamily={FONT} fontSize="8" fontWeight="800" fill="#ffffff">AMEX</text>
        </Logo>
    );
}

export function DiscoverLogo() {
    return (
        <Logo label="Discover" width="80">
            <text x="0" y="14.5" fontFamily={FONT} fontSize="11.5" fontWeight="800" fill="#1a1a2e">DISCOVER</text>
            <path fill="#f76b1c" d="M70 12.5c3.4 0 6.2-1.4 8-3.6-1.4 3.6-4.6 6.1-8.4 6.1-1.6 0-3.1-.4-4.4-1.2 1.4.5 3 .7 4.8.7z" />
        </Logo>
    );
}

export function JcbLogo() {
    return (
        <Logo label="JCB" width="54">
            <rect x="0" y="1" width="8" height="18" rx="2" fill="#0e4c96" />
            <rect x="9" y="1" width="8" height="18" rx="2" fill="#d31b2c" />
            <rect x="18" y="1" width="8" height="18" rx="2" fill="#00a650" />
            <text x="30" y="14.5" fontFamily={FONT} fontSize="11" fontWeight="800" fill="#1a1a2e">JCB</text>
        </Logo>
    );
}

export function UnionPayLogo() {
    return (
        <Logo label="UnionPay" width="86">
            <rect x="0" y="1" width="12" height="18" rx="3" fill="#d8232a" />
            <rect x="6" y="1" width="12" height="18" rx="3" fill="#00447c" />
            <rect x="12" y="1" width="12" height="18" rx="3" fill="#007b84" />
            <text x="28" y="14.5" fontFamily={FONT} fontSize="11.5" fontWeight="700" fill="#1a1a2e">UnionPay</text>
        </Logo>
    );
}

/* --------------------------- Wallets & BNPL --------------------------- */

export function PayPalLogo() {
    return (
        <Logo label="PayPal" width="74">
            <path fill="#003087" d="M7.2 2.5h6.1c3.5 0 5.5 1.7 5 4.9-.5 3.4-2.9 5.1-6.4 5.1H9.7l-.9 5.5H5.9L7.2 2.5z" />
            <path fill="#009cde" d="M10.9 7h5.5c3.1 0 4.9 1.5 4.5 4.4-.5 3.1-2.6 4.6-5.7 4.6h-1.8l-.8 5H8.9L10.9 7z" />
            <text x="24" y="15" fontFamily={FONT} fontSize="13.5" fontWeight="700" fill="#003087">Pay</text>
            <text x="45" y="15" fontFamily={FONT} fontSize="13.5" fontWeight="700" fill="#009cde">Pal</text>
        </Logo>
    );
}

export function StripeLogo() {
    return <PlainTextLogo label="Stripe" text="stripe" color="#635bff" width="50" fontSize="15" />;
}

export function KlarnaLogo() {
    return (
        <Logo label="Klarna" width="58">
            <rect x="0" y="1" width="56" height="18" rx="4" fill="#ffb3c7" />
            <text x="6" y="14.5" fontFamily={FONT} fontSize="11.5" fontWeight="700" fill="#0a0b09">Klarna</text>
        </Logo>
    );
}

export function AfterpayLogo() {
    return (
        <Logo label="Afterpay" width="74">
            <rect x="0" y="1" width="72" height="18" rx="4" fill="#b2fce4" />
            <text x="6" y="14.5" fontFamily={FONT} fontSize="11.5" fontWeight="700" fill="#0a0b09">afterpay</text>
        </Logo>
    );
}

/* ------------------------------ Europe ------------------------------- */

export function IdealLogo() {
    return <PlainTextLogo label="iDEAL" text="iDEAL" color="#cc0066" width="50" fontSize="14" />;
}

export function BancontactLogo() {
    return (
        <Logo label="Bancontact" width="102">
            <rect x="0" y="1" width="18" height="18" rx="4" fill="#005498" />
            <path fill="#ffd800" d="M4.2 12.6l3.1-5.2h2.4l-3.1 5.2z" />
            <path fill="#ffffff" d="M9.1 12.6l3.1-5.2h2.4l-3.1 5.2z" />
            <text x="23" y="15" fontFamily={FONT} fontSize="13" fontWeight="600" fill="#005498">Bancontact</text>
        </Logo>
    );
}

export function BlikLogo() {
    return (
        <Logo label="BLIK" width="50">
            <rect x="0" y="1" width="18" height="18" rx="4" fill="#000000" />
            <text x="5.6" y="15" fontFamily={FONT} fontSize="13" fontWeight="700" fill="#ffffff">b</text>
            <text x="23" y="15" fontFamily={FONT} fontSize="13" fontWeight="700" fill="#000000">BLIK</text>
        </Logo>
    );
}

export function EpsLogo() {
    return (
        <Logo label="EPS" width="20">
            <rect x="0" y="1" width="18" height="18" rx="4" fill="#1a1a2e" />
            <text x="3.4" y="14.4" fontFamily={FONT} fontSize="8.5" fontWeight="700" fill="#ffffff">eps</text>
        </Logo>
    );
}

export function GiropayLogo() {
    return (
        <Logo label="giropay" width="64">
            <text x="0" y="15" fontFamily={FONT} fontSize="13" fontWeight="700" fill="#000268">giro</text>
            <text x="27" y="15" fontFamily={FONT} fontSize="13" fontWeight="700" fill="#d8232a">pay</text>
        </Logo>
    );
}

export function Przelewy24Logo() {
    return (
        <Logo label="Przelewy24" width="20">
            <rect x="0" y="1" width="18" height="18" rx="4" fill="#d13239" />
            <text x="2.6" y="14.4" fontFamily={FONT} fontSize="8.5" fontWeight="700" fill="#ffffff">P24</text>
        </Logo>
    );
}

export function TwintLogo() {
    return (
        <Logo label="TWINT" width="70">
            <rect x="0" y="1" width="18" height="18" rx="4" fill="#000000" />
            <path fill="#ffffff" d="M4.6 6.4h8.8v2.1H4.6zM4.6 11.5h8.8v2.1H4.6z" />
            <text x="23" y="15" fontFamily={FONT} fontSize="13" fontWeight="700" fill="#000000">TWINT</text>
        </Logo>
    );
}

/* --------------------------- Global / regional --------------------------- */

export function AlipayLogo() {
    return <PlainTextLogo label="Alipay" text="Alipay" color="#1677ff" width="54" fontSize="13" />;
}

export function WechatPayLogo() {
    return <PlainTextLogo label="WeChat Pay" text="WeChat Pay" color="#07c160" width="82" fontSize="12.5" />;
}

export function PixLogo() {
    return <PlainTextLogo label="Pix" text="Pix" color="#32bcad" width="34" fontSize="14" />;
}

export function UpiLogo() {
    return <PlainTextLogo label="UPI" text="UPI" color="#097939" width="34" fontSize="14" fontWeight="800" />;
}

export const PAYMENT_LOGOS = [
    // Cards
    { name: 'Visa', Logo: VisaLogo },
    { name: 'Mastercard', Logo: MastercardLogo },
    { name: 'American Express', Logo: AmexLogo },
    { name: 'Discover', Logo: DiscoverLogo },
    { name: 'JCB', Logo: JcbLogo },
    { name: 'UnionPay', Logo: UnionPayLogo },
    // Wallets & BNPL
    { name: 'PayPal', Logo: PayPalLogo },
    { name: 'Stripe', Logo: StripeLogo },
    { name: 'Klarna', Logo: KlarnaLogo },
    { name: 'Afterpay', Logo: AfterpayLogo },
    // Europe
    { name: 'iDEAL', Logo: IdealLogo },
    { name: 'Bancontact', Logo: BancontactLogo },
    { name: 'BLIK', Logo: BlikLogo },
    { name: 'EPS', Logo: EpsLogo },
    { name: 'giropay', Logo: GiropayLogo },
    { name: 'Przelewy24', Logo: Przelewy24Logo },
    { name: 'TWINT', Logo: TwintLogo },
    // Global / regional
    { name: 'Alipay', Logo: AlipayLogo },
    { name: 'WeChat Pay', Logo: WechatPayLogo },
    { name: 'Pix', Logo: PixLogo },
    { name: 'UPI', Logo: UpiLogo },
];
